export const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
export const normalizeHex = (raw) => {
    const trimmed = raw.trim().replace(/^#/, '').toUpperCase();
    if (/^[0-9A-F]{6}$/.test(trimmed)) {
        return `#${trimmed}`;
    }
    if (/^[0-9A-F]{3}$/.test(trimmed)) {
        return `#${trimmed
            .split('')
            .map(ch => ch + ch)
            .join('')}`;
    }
    return null;
};
export const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};
export const rgbToHex = (r, g, b) => {
    const to = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0').toUpperCase();
    return `#${to(r)}${to(g)}${to(b)}`;
};
export const rgbToHsv = (r, g, b) => {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === rn) {
            h = ((gn - bn) / d) % 6;
        }
        else if (max === gn) {
            h = (bn - rn) / d + 2;
        }
        else {
            h = (rn - gn) / d + 4;
        }
        h *= 60;
        if (h < 0) {
            h += 360;
        }
    }
    const s = max === 0 ? 0 : d / max;
    return { h, s, v: max };
};
export const hsvToRgb = (h, s, v) => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let rp = 0;
    let gp = 0;
    let bp = 0;
    if (h < 60) {
        rp = c;
        gp = x;
    }
    else if (h < 120) {
        rp = x;
        gp = c;
    }
    else if (h < 180) {
        gp = c;
        bp = x;
    }
    else if (h < 240) {
        gp = x;
        bp = c;
    }
    else if (h < 300) {
        rp = x;
        bp = c;
    }
    else {
        rp = c;
        bp = x;
    }
    return {
        r: (rp + m) * 255,
        g: (gp + m) * 255,
        b: (bp + m) * 255,
    };
};
export const hsvToHex = (hsv) => {
    const { r, g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v);
    return rgbToHex(r, g, b);
};
export const hexLuminance = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const toLin = (c) => {
        const n = c / 255;
        return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
};
/** Subtle texture stroke that still contrasts on the fill. */
export const derivePatternColor = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const dark = hexLuminance(hex) > 0.55;
    const t = dark ? 0.18 : 0.22;
    const mix = (channel) => dark ? channel * (1 - t) : channel + (255 - channel) * t;
    return rgbToHex(mix(r), mix(g), mix(b));
};
/** Dense spectrum shortcuts — many hues × tones. */
export const buildSpectrumPresets = () => {
    const hues = [0, 20, 40, 60, 90, 120, 160, 190, 210, 240, 270, 300, 330];
    const tones = [
        [0.35, 1],
        [0.55, 1],
        [0.8, 1],
        [1, 1],
        [1, 0.75],
        [1, 0.5],
        [0.2, 0.35],
    ];
    const neutrals = [
        '#FFFFFF',
        '#F8FAFC',
        '#F5F5F2',
        '#E2E8F0',
        '#CBD5E1',
        '#94A3B8',
        '#64748B',
        '#334155',
        '#1E293B',
        '#0F172A',
        '#000000',
    ];
    const colors = [...neutrals];
    for (const [s, v] of tones) {
        for (const h of hues) {
            colors.push(hsvToHex({ h, s, v }));
        }
    }
    return colors;
};
